import React from 'react'
import { Divider, Stack, Typography } from '@mui/material'
import DocLink from '../../components/DocLink'
import { Bullet, Bullets, Info, Step, Warning } from '../../components/DocsComponents'

const Subcontractors = () => (
  <Stack gap={3} divider={<Divider />}>
    <Stack gap={1}>
      <Typography variant="title3">What subcontractors are</Typography>
      <Typography>
        Subcontractors are external clinicians — bank staff, freelancers, or
        crew supplied by another provider — who work your shifts but are not
        part of your workforce. Instead of a full employee record, they get a
        lightweight account with a single clinical role and a{' '}
        <strong>time-boxed access window</strong>: once the window ends they
        can no longer sign in until access is granted again.
      </Typography>
      <Typography>
        When a subcontractor signs in they see a restricted view focused on
        clinical tools (ePCR, medicine administration, VDIs, and incident
        reporting) rather than the full staff sidebar. They have no payroll
        profile, training record, or availability — see{' '}
        <DocLink
          docsPage
          url="/employees-hr/working-with-subcontractors"
          text="Working with Subcontractors"
        />
        for what they can do and how assignment works.
      </Typography>
      <Info title="Who can do this?">
        Subcontractors are managed by the <strong>Personnel</strong> team
        (site administrators always have access) from{' '}
        <DocLink text="Employees > Subcontractors" url="/employees/subcontractors" />
        . Each subcontractor row opens a detail page with their access
        history and controls.
      </Info>
    </Stack>

    <Stack gap={1}>
      <Typography variant="title3">Adding a subcontractor</Typography>
      <Step number={1} title="Create the account">
        On <DocLink text="Employees > Subcontractors" url="/employees/subcontractors" />
        , choose <strong>New Subcontractor</strong> and enter their first
        name, last name, email address, and clinical role (picked from your
        organisation&apos;s configured roles). The email address must not
        belong to an existing employee — a person cannot be both. To take a
        subcontractor on as staff, convert them instead (see{' '}
        <strong>Converting to an employee</strong> below).
      </Step>
      <Step number={2} title="They verify their email">
        The subcontractor receives a{' '}
        <strong>Verify your subcontractor account</strong> email with a link
        to set a password. Until they do, their account shows as{' '}
        <strong>Pending Verification</strong>. You can resend the email from
        the list or their detail page if it goes astray.
      </Step>
      <Step number={3} title="Their access window starts">
        Setting their password activates the account: their status becomes{' '}
        <strong>Active</strong> and their first access window starts from
        that moment — not from when the invitation was sent, so an invite
        sitting unread does not eat into their access time.
      </Step>
      <Info>
        Subcontractors who are <strong>Pending Verification</strong> can
        already be assigned to shifts — verification only gates their own
        sign-in, not your planning.
      </Info>
    </Stack>

    <Stack gap={1}>
      <Typography variant="title3">The access window</Typography>
      <Typography>
        The default window length is set at{' '}
        <DocLink
          url="/site-configuration/subcontractors"
          text="Configuration > Employees > Subcontractors"
        />{' '}
        and applies each time a subcontractor account is activated — on first
        verification and on every reactivation. Choose from 1 day, 3 days, 1
        week (the default), 2 weeks, 1 month, 3 months, or{' '}
        <strong>Indefinite — never expires</strong>.
      </Typography>
      <Typography>
        Changing this setting does not shorten or extend windows that are
        already open — it only affects future activations.
      </Typography>
      <Warning title="Indefinite access">
        With an indefinite window, an account stays active until access is
        revoked or the account is archived. AmbuNet asks you to confirm
        before enabling it, and shows a warning banner on any subcontractor
        who has it. Review indefinite accounts regularly and revoke access
        when the engagement ends.
      </Warning>
    </Stack>

    <Stack gap={1}>
      <Typography variant="title3">
        Granting, extending, and revoking access
      </Typography>
      <Typography>
        The <strong>Access</strong> panel on a subcontractor&apos;s detail
        page shows their current window (valid from / valid until, or
        &quot;Never expires&quot;), who last activated them, and when. From
        here you can:
      </Typography>
      <Bullets>
        <Bullet>
          <strong>Grant Access</strong> / <strong>Extend Access</strong> —
          opens a dialog with a duration picker (the same options as the
          organisation default, including indefinite). The new window starts
          immediately and replaces any existing window.
        </Bullet>
        <Bullet>
          <strong>Revoke Access</strong> — immediately ends an active
          subcontractor&apos;s access, including any signed-in session. They
          cannot log in again until access is re-granted.
        </Bullet>
        <Bullet>
          <strong>Resend Verification</strong> — shown while they are still
          Pending Verification.
        </Bullet>
      </Bullets>
      <Typography>
        The list page also has a one-click reactivate action on each row that
        grants a fresh window using the organisation default, without opening
        the detail page.
      </Typography>
      <Info title="Expiry is enforced everywhere">
        When a window lapses or access is revoked, the subcontractor is
        blocked at the login screen and any active session is ended on their
        very next action — there is no grace period.
      </Info>
    </Stack>

    <Stack gap={1}>
      <Typography variant="title3">Statuses at a glance</Typography>
      <Bullets>
        <Bullet>
          <strong>Pending Verification</strong> (amber) — invited but has not
          yet set a password. Assignable to shifts; cannot sign in yet.
        </Bullet>
        <Bullet>
          <strong>Active</strong> (green) — verified with a current access
          window. Can sign in and be assigned to shifts.
        </Bullet>
        <Bullet>
          <strong>Expired</strong> (red) — their window lapsed or access was
          revoked. Cannot sign in or be newly assigned until reactivated.
        </Bullet>
        <Bullet>
          <strong>Archived</strong> (grey) — the engagement has ended.
          Hidden from the list by default (use the archive toggle to show
          them) and excluded from staff searches.
        </Bullet>
      </Bullets>
    </Stack>

    <Stack gap={1}>
      <Typography variant="title3">Clinical role</Typography>
      <Typography>
        Each subcontractor has exactly one clinical role, edited from the{' '}
        <strong>Permissions</strong> panel on their detail page. It controls
        which shifts and clinical duties they can be assigned to, and is
        auto-filled into a shift&apos;s role when you assign them. If your
        role list has changed since they were created, their existing role is
        kept selectable until you change it.
      </Typography>
      <Typography>
        A subcontractor&apos;s name and email address cannot be edited after
        creation — if they were entered incorrectly, archive the record and
        add them again.
      </Typography>
    </Stack>

    <Stack gap={1}>
      <Typography variant="title3">Archiving</Typography>
      <Typography>
        Archive a subcontractor when the engagement ends — from their detail
        page or the row action on the list. Archiving keeps their history but
        removes them from staff searches and blocks sign-in. Unarchiving
        restores them to whatever their access implies: <strong>Active</strong>{' '}
        if their window is still open, <strong>Expired</strong> if it lapsed,
        or <strong>Pending Verification</strong> if they never verified.
      </Typography>
    </Stack>

    <Stack gap={1}>
      <Typography variant="title3">Converting to an employee</Typography>
      <Typography>
        If you take a subcontractor on as staff, choose{' '}
        <strong>Convert to Employee</strong> on their detail page. Pick their
        base location(s), clinical role, start date and (optionally) employment
        type. They keep the same login and password.
      </Typography>
      <Bullets>
        <Bullet>
          They become a <strong>New Starter</strong> employee with your
          standard onboarding checklist, and use one of your employee seats.
        </Bullet>
        <Bullet>
          Shifts they are booked on that haven&apos;t started yet move to
          their employee record. Shifts already worked stay recorded as
          worked by a subcontractor.
        </Bullet>
        <Bullet>
          The subcontractor record is archived (with a link to the employee)
          and can&apos;t be reactivated. Patient records they were involved
          in stay linked to them.
        </Bullet>
        <Bullet>
          Employee compliance records — DBS, training, driving licence — start
          empty, so add them from the employee&apos;s page.
        </Bullet>
      </Bullets>
    </Stack>
  </Stack>
)

export default Subcontractors
